/**
 * 存储工具函数
 */

/**
 * 存储键名常量
 */
export const STORAGE_KEYS = {
  TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_INFO: 'user_info',
  USER_SETTINGS: 'user_settings',
  THEME: 'theme',
  LANGUAGE: 'language',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed',
  TABS_VIEW: 'tabs_view',
  REMEMBER_PASSWORD: 'remember_password',
  LAST_USERNAME: 'last_username',
  MENU_DATA: 'menu_data',
  PERMISSION_DATA: 'permission_data',

  // REQ-20260508-0001: 多租户中间 token（5min TTL，仅用于 /tenant-select 流程）
  SELECT_TENANT_TOKEN: 'select_tenant_token',
  // REQ-20260508-0001: 多租户列表缓存（避免刷新页面后丢失，sessionStorage 推荐）
  AVAILABLE_TENANTS: 'available_tenants',
  // REQ-20260508-0001: 当前会话门户偏好（remember=false 时仅 sessionStorage 短期保留）
  PORTAL_PREFERENCE_SESSION: 'portal_preference_session',
} as const;

/**
 * 获取存储的值
 * @param key 存储键
 * @param defaultValue 默认值
 * @param storage 存储对象
 */
export function getStorageItem<T>(key: string, defaultValue: T, storage?: Storage): T;
export function getStorageItem<T>(key: string): T | undefined;
export function getStorageItem<T = any>(
  key: string,
  defaultValue?: T,
  storage: Storage = localStorage
): T | undefined {
  try {
    const item = storage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    console.error(`获取存储项 ${key} 失败:`, error);
    return defaultValue;
  }
}

/**
 * 设置存储的值
 * @param key 存储键
 * @param value 存储值
 * @param storage 存储对象
 */
export const setStorageItem = (
  key: string,
  value: any,
  storage: Storage = localStorage
): boolean => {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`设置存储项 ${key} 失败:`, error);
    return false;
  }
};

/**
 * 移除存储的值
 * @param key 存储键
 * @param storage 存储对象
 */
export const removeStorageItem = (
  key: string,
  storage: Storage = localStorage
): boolean => {
  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`移除存储项 ${key} 失败:`, error);
    return false;
  }
};

/**
 * 清空存储
 * @param storage 存储对象
 */
export const clearStorage = (storage: Storage = localStorage): boolean => {
  try {
    storage.clear();
    return true;
  } catch (error) {
    console.error('清空存储失败:', error);
    return false;
  }
};

/**
 * 获取存储大小（仅支持localStorage）
 */
export const getStorageSize = (): string => {
  try {
    let total = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return (total / 1024).toFixed(2) + ' KB';
  } catch (error) {
    console.error('获取存储大小失败:', error);
    return '0 KB';
  }
};

/**
 * 检查存储是否可用
 * @param storage 存储对象
 */
export const isStorageAvailable = (storage: Storage = localStorage): boolean => {
  try {
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

// Token相关存储操作
// 注意：Token 使用直接存储方式，不进行 JSON 序列化，与其他字段不同
export const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

export const setToken = (token: string): boolean => {
  try {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    return true;
  } catch (error) {
    console.error('设置 Token 失败:', error);
    return false;
  }
};

export const removeToken = (): boolean => {
  try {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    return true;
  } catch (error) {
    console.error('移除 Token 失败:', error);
    return false;
  }
};

// Refresh Token - 使用直接存储方式（与 access_token 保持一致，不进行 JSON 序列化）
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

export const setRefreshToken = (token: string): boolean => {
  try {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    return true;
  } catch (error) {
    console.error('设置 Refresh Token 失败:', error);
    return false;
  }
};

export const removeRefreshToken = (): boolean => {
  try {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    return true;
  } catch (error) {
    console.error('移除 Refresh Token 失败:', error);
    return false;
  }
};

// 用户信息相关存储操作
export const getUserInfo = (): any => {
  return getStorageItem(STORAGE_KEYS.USER_INFO);
};

export const setUserInfo = (userInfo: any): boolean => {
  return setStorageItem(STORAGE_KEYS.USER_INFO, userInfo);
};

export const removeUserInfo = (): boolean => {
  return removeStorageItem(STORAGE_KEYS.USER_INFO);
};

// 用户设置相关存储操作
export const getUserSettings = (): any => {
  return getStorageItem(STORAGE_KEYS.USER_SETTINGS, {});
};

export const setUserSettings = (settings: any): boolean => {
  const currentSettings = getUserSettings();
  const newSettings = { ...currentSettings, ...settings };
  return setStorageItem(STORAGE_KEYS.USER_SETTINGS, newSettings);
};

export const removeUserSettings = (): boolean => {
  return removeStorageItem(STORAGE_KEYS.USER_SETTINGS);
};

// 主题相关存储操作
export const getTheme = (): string => {
  return getStorageItem(STORAGE_KEYS.THEME, 'light');
};

export const setTheme = (theme: string): boolean => {
  return setStorageItem(STORAGE_KEYS.THEME, theme);
};

// 语言相关存储操作
export const getLanguage = (): string => {
  return getStorageItem(STORAGE_KEYS.LANGUAGE, 'zh-CN');
};

export const setLanguage = (language: string): boolean => {
  return setStorageItem(STORAGE_KEYS.LANGUAGE, language);
};

// 侧边栏折叠状态
export const getSidebarCollapsed = (): boolean => {
  return getStorageItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, false);
};

export const setSidebarCollapsed = (collapsed: boolean): boolean => {
  return setStorageItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, collapsed);
};

// 标签页视图状态
export const getTabsView = (): boolean => {
  return getStorageItem(STORAGE_KEYS.TABS_VIEW, true);
};

export const setTabsView = (enabled: boolean): boolean => {
  return setStorageItem(STORAGE_KEYS.TABS_VIEW, enabled);
};

// 记住密码状态
export const getRememberPassword = (): boolean => {
  return getStorageItem(STORAGE_KEYS.REMEMBER_PASSWORD, false);
};

export const setRememberPassword = (remember: boolean): boolean => {
  return setStorageItem(STORAGE_KEYS.REMEMBER_PASSWORD, remember);
};

// 最后登录用户名
export const getLastUsername = (): string => {
  return getStorageItem(STORAGE_KEYS.LAST_USERNAME, '');
};

export const setLastUsername = (username: string): boolean => {
  return setStorageItem(STORAGE_KEYS.LAST_USERNAME, username);
};

// 菜单数据缓存
export const getMenuData = (): any[] => {
  return getStorageItem(STORAGE_KEYS.MENU_DATA, []);
};

export const setMenuData = (menuData: any[]): boolean => {
  return setStorageItem(STORAGE_KEYS.MENU_DATA, menuData);
};

export const removeMenuData = (): boolean => {
  return removeStorageItem(STORAGE_KEYS.MENU_DATA);
};

// 权限数据缓存
export const getPermissionData = (): any[] => {
  return getStorageItem(STORAGE_KEYS.PERMISSION_DATA, []);
};

export const setPermissionData = (permissionData: any[]): boolean => {
  return setStorageItem(STORAGE_KEYS.PERMISSION_DATA, permissionData);
};

export const removePermissionData = (): boolean => {
  return removeStorageItem(STORAGE_KEYS.PERMISSION_DATA);
};

/**
 * 清除用户相关数据
 */
export const clearUserData = (): void => {
  removeToken();
  removeRefreshToken();
  removeUserInfo();
  removeUserSettings();
  removeMenuData();
  removePermissionData();
  removeStorageItem('menu_cache_business');
  removeStorageItem('menu_cache_admin');
};

/**
 * 清除应用设置数据
 */
export const clearAppSettings = (): void => {
  removeStorageItem(STORAGE_KEYS.THEME);
  removeStorageItem(STORAGE_KEYS.LANGUAGE);
  removeStorageItem(STORAGE_KEYS.SIDEBAR_COLLAPSED);
  removeStorageItem(STORAGE_KEYS.TABS_VIEW);
};

/**
 * 存储工具类
 */
export class StorageUtil {
  private storage: Storage;

  constructor(storage: Storage = localStorage) {
    this.storage = storage;
  }

  get<T = any>(key: string, defaultValue?: T): T | undefined {
    return getStorageItem(key, defaultValue, this.storage);
  }

  set(key: string, value: any): boolean {
    return setStorageItem(key, value, this.storage);
  }

  remove(key: string): boolean {
    return removeStorageItem(key, this.storage);
  }

  clear(): boolean {
    return clearStorage(this.storage);
  }

  has(key: string): boolean {
    return this.storage.getItem(key) !== null;
  }

  size(): string {
    if (this.storage === localStorage) {
      return getStorageSize();
    }
    return '0 KB';
  }

  isAvailable(): boolean {
    return isStorageAvailable(this.storage);
  }
}

// 创建默认实例
export const localStorageUtil = new StorageUtil(localStorage);
export const sessionStorageUtil = new StorageUtil(sessionStorage);
